import type * as React from 'react';

import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { css, styled, type WebTarget } from 'styled-components';

import { Icon } from '@creditornot/alchemy-react';
import {
  banknote,
  bike,
  calendar,
  chart,
  gauge,
  gear,
  globe,
  house,
  pin,
} from '@creditornot/alchemy-react/icons';
import { elevationClassName, typographyCss } from '@creditornot/cb-ingredients';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { MenuAccordionItem, MenuItemGroup } from '@creditornot/cb-menu';

import { AccessControl } from 'common/components/AccessControl';
import TextButtonLinkRRD from 'common/components/TextButtonLinkRRD';
import { MIDNIGHT_BLUE_TOKEN, ZIndex } from 'common/constants';
import {
  useMatchAllCouriers,
  useMatchCourierDetails,
  useMatchCourierGroups,
  useMatchCourierOnboarding,
  useMatchDashboard,
  useMatchEarnings,
  useMatchGeoLocation,
  useMatchGeoLocationHistory,
  useMatchLiveDeliveries,
  useMatchOverview,
  useMatchPerformance,
  useMatchSchedule,
  useMatchSettings,
} from 'common/hooks/useAppRoutes';
import useConfigUI from 'common/hooks/useConfigUI';
import useConfigurationForScheduling from 'common/hooks/useConfigurationForScheduling';
import useUserPermissions from 'common/hooks/useUserPermissions';
import { media, useIsSmallDevice } from 'common/media';
import { isCompanySelfDeliveryOrSaas, noop } from 'common/utils';

import Header from './Header';
import { useCurrentCompany } from './hooks';
import UserInfoWithMenu from './UserInfoWithMenu';

const DRAWER = {
  WEB: {
    WIDTH: {
      COLLAPSED: '88px',
      EXPANDED: '324px',
    },
    HEIGHT: 'auto',
  },
  MOBILE: {
    HEIGHT: '80px',
  },
};

const Root = styled.div<{ $isSidebarCollapsed?: boolean; $isMatchLiveDeliveries: boolean }>`
  ${typographyCss.Body3({ weight: 'semiBold' })}
  background-color: ${MIDNIGHT_BLUE_TOKEN.COLOR.BG};
  padding: 24px 0 0 0;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${ZIndex.Sidebar};
  height: ${props => (props.$isSidebarCollapsed ? DRAWER.MOBILE.HEIGHT : DRAWER.WEB.HEIGHT)};

  @media ${media.sm} {
    position: relative;
    width: ${props =>
      props.$isSidebarCollapsed ? DRAWER.WEB.WIDTH.COLLAPSED : DRAWER.WEB.WIDTH.EXPANDED};
    height: 100%;
  }
`;

const Layout = styled.div<{ $isSidebarCollapsed?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;

  ${props => {
    if (props.$isSidebarCollapsed) {
      return `
        overflow: hidden;
        height: ${DRAWER.MOBILE.HEIGHT};
      `;
    }
    return `
        height: 100vh;
        overflow: scroll;
      `;
  }}
  @media ${media.sm} {
    display: flex;
    width: ${props =>
      props.$isSidebarCollapsed ? DRAWER.WEB.WIDTH.COLLAPSED : DRAWER.WEB.WIDTH.EXPANDED};
    flex-direction: column;
    align-items: initial;
    height: 100%;
    overflow-y: auto;
  }
`;

const StyledHeader = styled<{ $isSidebarCollapsed?: boolean } & WebTarget>(Header)`
  padding: 16px 16px 0 16px;

  .logo {
    padding: 12px 16px;
    align-items: center;
    display: ${props => (props.$isSidebarCollapsed ? 'none' : 'flex')};
  }

  @media ${media.sm} {
    ${props => {
      if (props.$isSidebarCollapsed) {
        return `
          padding: 12px 12px 0 12px;
          justify-content: center;
        `;
      }
    }};
  }
`;

const StyledMenuItemGroup = styled(MenuItemGroup)`
  margin: 0;
  padding: 16px;

  > div {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  > div > [data-cb-menu-item] {
    margin: 0;
    border-radius: 8px;
    width: 100%;

    &:hover {
      background: ${MIDNIGHT_BLUE_TOKEN.COLOR.HOVER};
    }
  }

  a {
    color: ${color.fixedWhite} !important;

    svg {
      fill: ${color.fixedWhite} !important;
    }
    &:hover {
      color: ${color.fixedWhite};
      background: ${MIDNIGHT_BLUE_TOKEN.COLOR.HOVER};
    }
  }

  @media ${media.sm} {
    padding-top: 40px;
    justify-content: center;
  }
`;

const StyledMenuAccordionItem = styled<
  { $isSidebarCollapsed?: boolean; $active?: boolean } & WebTarget
>(MenuAccordionItem)`
  ${typographyCss.body}
  color: ${color.fixedWhite};
  padding: 12px 16px;
  margin: 0;
  font-weight: 600 !important;

  + div {
    height: 0 !important;
  }

  & > div:first-child {
    overflow: visible;
  }
  & > div > svg {
    display: none;
  }
`;

const MenuItemCouriers = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const Footer = styled.div<{ $hideFooter?: boolean }>`
  width: 100%;
  padding: 50px 16px 16px 16px;
  position: sticky;
  margin-bottom: 16px;
  bottom: 0;
  display: ${props => (props.$hideFooter ? 'none' : 'flex')};
  & > button {
    padding: 0;
  }

  @media ${media.sm} {
    padding: 50px 16px 16px 16px;
  }
`;

const CourierBikeContainer = styled.div`
  width: 24px;
  height: 24px;
`;

const StyledTextButtonLinkRRD = styled<{ $active?: boolean } & WebTarget>(TextButtonLinkRRD)`
  ${typographyCss.body}
  font-weight: 600 !important;
  padding: 12px 16px;
  margin: 0;
  width: 100%;
  border-radius: 8px;
  background: ${props => (props.$active ? MIDNIGHT_BLUE_TOKEN.COLOR.ACTIVE : 'transparent')};
  gap: 16px;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const StyledTextButtonLinkRRDNested = styled(StyledTextButtonLinkRRD)`
  padding: 0;
  background: transparent;
  opacity: 0.5;

  &:hover {
    background: none !important;
  }

  span {
    margin-left: 48px;
    padding: 8px 16px;
    border-radius: 8px;
    background: ${props => (props.$active ? MIDNIGHT_BLUE_TOKEN.COLOR.ACTIVE : 'transparent')};

    &:hover {
      background: ${MIDNIGHT_BLUE_TOKEN.COLOR.HOVER};
    }
  }
`;

const userInfoFooterStyles = (props: { $isSidebarCollapsed?: boolean }) => css`
  justify-content: ${props.$isSidebarCollapsed ? 'center' : ''};
  display: ${props.$isSidebarCollapsed ? 'flex' : 'grid'};
`;

const StyledUserInfoWithMenu = styled<{ $isSidebarCollapsed?: boolean } & WebTarget>(
  UserInfoWithMenu,
)`
  .info {
    display: ${props => (props.$isSidebarCollapsed ? 'none !important' : 'flex')};
  }

  @media ${media.sm} {
    &.footer {
      ${props => userInfoFooterStyles({ $isSidebarCollapsed: props.$isSidebarCollapsed })}
    }
  }
`;

const SidebarMainContentWrapper = styled.div`
  width: 100%;
`;

const useCustomizedSearchParams = () => {
  const isMatchLiveDeliveries = Boolean(useMatchLiveDeliveries());
  const isMatchGeoLocationHistory = Boolean(useMatchGeoLocationHistory());
  const [searchParams] = useSearchParams();

  // Remove `courierId` if user navigates away from "Live deliveries"
  if (isMatchLiveDeliveries) {
    searchParams.delete('courierId');
  }

  if (isMatchGeoLocationHistory) {
    searchParams.delete('courierId');
    searchParams.delete('date');
  }

  return `${searchParams.size ? '?' : ''}${searchParams.toString()}`;
};

const NavigationLinks: React.FC<{
  isSidebarCollapsed: boolean;
  showSidebar: () => void;
  hideSidebar?: () => void;
}> = ({ isSidebarCollapsed, showSidebar, hideSidebar = noop }) => {
  const isMatchDashboard = Boolean(useMatchDashboard());
  const isMatchAllCouriers = Boolean(useMatchAllCouriers());
  const isMatchCourierDetails = Boolean(useMatchCourierDetails());
  const isMatchCourierGroups = Boolean(useMatchCourierGroups());
  const isMatchGeoLocation = Boolean(useMatchGeoLocation());
  const isMatchLiveDeliveries = Boolean(useMatchLiveDeliveries());
  const isMatchCouierOnboarding = Boolean(useMatchCourierOnboarding());
  const isMatchPerformance = Boolean(useMatchPerformance());
  const isMatchEarnings = Boolean(useMatchEarnings());
  const isMatchGeoLocationHistory = Boolean(useMatchGeoLocationHistory());
  const isMatchSchedule = Boolean(useMatchSchedule());
  const isMatchOverview = Boolean(useMatchOverview());
  const isMatchSettings = Boolean(useMatchSettings());
  const search = useCustomizedSearchParams();
  const { data: config } = useConfigUI();
  const { currentCompany } = useCurrentCompany();
  const { id: companyId, partnership, countryCode } = currentCompany.data ?? {};

  const configurationForScheduling = useConfigurationForScheduling({
    companyId,
    countryCode,
  });

  const isOverviewPageAccessible =
    partnership !== undefined &&
    !isCompanySelfDeliveryOrSaas(currentCompany.data) &&
    config.mainNavigation.overview;

  const isSchedulePageAccessible =
    configurationForScheduling.data?.configValues.enable_scheduling.value ?? false;

  const { isCurrentCompanyPartnershipLCSP, isCurrentCompanyPartnershipAvailable } =
    currentCompany.data ?? {};

  const { data: rolePermissions } = useUserPermissions({
    companyId,
  });

  const userCanViewRoleManagement = rolePermissions?.permissions?.role_management?.has('r');

  const shouldShowSettingsTab = isCurrentCompanyPartnershipAvailable
    ? !isCurrentCompanyPartnershipLCSP ||
      (isCurrentCompanyPartnershipLCSP && userCanViewRoleManagement)
    : false;

  const settingsTabLink = isCurrentCompanyPartnershipLCSP
    ? `/settings/role-management${search}`
    : `/settings/company${search}`;

  const isCouriersRouteMatched =
    isMatchAllCouriers || isMatchCourierGroups || isMatchCourierDetails || isMatchCouierOnboarding;

  const [isCourierSectionManualCollapsed, setIsCourierSectionManualCollapsed] = useState<
    undefined | boolean
  >();

  const isCourierSectionCollapsed =
    isCourierSectionManualCollapsed !== undefined
      ? isCourierSectionManualCollapsed
      : isCouriersRouteMatched;

  const handleCourierMenuItemOnClick = () => {
    if (isSidebarCollapsed) {
      setIsCourierSectionManualCollapsed(_ => {
        showSidebar();
        return true;
      });

      return;
    }

    setIsCourierSectionManualCollapsed(_ => !isCourierSectionCollapsed);
  };

  const handleMenuOnClick = () => {
    hideSidebar();
  };

  const showAllCouriersSubmenu =
    config.mainNavigation.allCouriers &&
    config.mainNavigation.courierGroups &&
    config.mainNavigation.onboarding;

  return (
    <StyledMenuItemGroup>
      {isOverviewPageAccessible && (
        <StyledTextButtonLinkRRD
          $active={isMatchOverview}
          icon={<Icon source={house} size="medium" />}
          onClick={handleMenuOnClick}
          to={`/overview/${search}`}
          variant={isMatchOverview ? 'brand' : 'neutral'}
        >
          {isSidebarCollapsed ? '' : 'Overview'}
        </StyledTextButtonLinkRRD>
      )}
      {showAllCouriersSubmenu && (
        <StyledMenuAccordionItem
          $isSidebarCollapsed={isSidebarCollapsed}
          $active={isCouriersRouteMatched}
          caretPosition="right"
          onClick={handleCourierMenuItemOnClick}
          open={isCourierSectionCollapsed}
        >
          <MenuItemCouriers>
            <CourierBikeContainer>
              <Icon source={bike} size="medium" />
            </CourierBikeContainer>
            <span> {isSidebarCollapsed ? '' : 'Couriers'}</span>
          </MenuItemCouriers>
        </StyledMenuAccordionItem>
      )}
      {!isSidebarCollapsed && isCourierSectionCollapsed && showAllCouriersSubmenu ? (
        <StyledTextButtonLinkRRDNested
          title="All couriers"
          to={`/couriers${search}`}
          onClick={handleMenuOnClick}
          $active={isMatchAllCouriers}
        >
          <span>All couriers</span>
        </StyledTextButtonLinkRRDNested>
      ) : null}

      {config.mainNavigation.courierGroups && !isSidebarCollapsed && isCourierSectionCollapsed ? (
        <StyledTextButtonLinkRRDNested
          title="Courier groups"
          to={`/courier-groups${search}`}
          onClick={handleMenuOnClick}
          $active={isMatchCourierGroups}
        >
          <span>Courier groups</span>
        </StyledTextButtonLinkRRDNested>
      ) : null}
      {config.mainNavigation.onboarding.sidebar &&
      !isSidebarCollapsed &&
      isCourierSectionCollapsed ? (
        <StyledTextButtonLinkRRDNested
          title="Onboarding"
          onClick={handleMenuOnClick}
          to={`/courier-onboarding${search}`}
          $active={isMatchCouierOnboarding}
        >
          <span>Onboarding</span>
        </StyledTextButtonLinkRRDNested>
      ) : null}

      {
        // This navigation link is only visible for LCSP users.
        // LCSP users sees this link when they visits:
        // 1. couriers listing
        // 2. couriers listing > upload couriers
        //   - this is different than Wolt use-case where they sees "Couriers > Onboarding"
        (config.mainNavigation.allCouriers || config.mainNavigation.onboarding.sidebar) &&
          !config.mainNavigation.courierGroups && (
            <StyledTextButtonLinkRRD
              $active={isMatchAllCouriers || isMatchCouierOnboarding}
              icon={<Icon source={bike} size="medium" />}
              onClick={handleMenuOnClick}
              to={`/couriers${search}`}
            >
              {isSidebarCollapsed ? '' : 'All couriers'}
            </StyledTextButtonLinkRRD>
          )
      }
      {config.mainNavigation.liveDeliveries && (
        <StyledTextButtonLinkRRD
          onClick={handleMenuOnClick}
          $active={isMatchGeoLocation || isMatchLiveDeliveries}
          $isSidebarCollapsed={isSidebarCollapsed}
          icon={<Icon source={pin} size="medium" />}
          to={`/live-deliveries${search}`}
        >
          {isSidebarCollapsed ? '' : 'Live deliveries'}
        </StyledTextButtonLinkRRD>
      )}

      {config.mainNavigation.geolocationHistory && (
        <StyledTextButtonLinkRRD
          onClick={handleMenuOnClick}
          $isSidebarCollapsed={isSidebarCollapsed}
          icon={<Icon source={globe} size="medium" />}
          to={`/geolocation-history${search}`}
          $active={isMatchGeoLocationHistory}
        >
          {isSidebarCollapsed ? '' : 'Geolocation history'}
        </StyledTextButtonLinkRRD>
      )}

      {config.mainNavigation.dashboard && (
        <AccessControl companyId={companyId} required={{ 'scheduling.dashboard': new Set(['r']) }}>
          <StyledTextButtonLinkRRD
            onClick={handleMenuOnClick}
            $isSidebarCollapsed={isSidebarCollapsed}
            icon={<Icon source={gauge} size="medium" />}
            to={`/dashboard${search}`}
            $active={isMatchDashboard}
          >
            {isSidebarCollapsed ? '' : 'Dashboard'}
          </StyledTextButtonLinkRRD>
        </AccessControl>
      )}
      {config.mainNavigation.performance && (
        <StyledTextButtonLinkRRD
          onClick={handleMenuOnClick}
          $isSidebarCollapsed={isSidebarCollapsed}
          icon={<Icon source={chart} size="medium" />}
          to={`/performance/couriers${search}`}
          $active={isMatchPerformance}
        >
          {isSidebarCollapsed ? '' : 'Performance'}
        </StyledTextButtonLinkRRD>
      )}
      {config.mainNavigation.financials && (
        <AccessControl companyId={companyId} required={{ earnings: new Set(['r']) }}>
          <StyledTextButtonLinkRRD
            onClick={handleMenuOnClick}
            $isSidebarCollapsed={isSidebarCollapsed}
            icon={<Icon source={banknote} size="medium" />}
            to={`/earnings/couriers${search}`}
            $active={isMatchEarnings}
          >
            {isSidebarCollapsed ? '' : 'Financials'}
          </StyledTextButtonLinkRRD>
        </AccessControl>
      )}
      {config.mainNavigation.schedule && isSchedulePageAccessible && (
        <StyledTextButtonLinkRRD
          onClick={handleMenuOnClick}
          $isSidebarCollapsed={isSidebarCollapsed}
          icon={<Icon source={calendar} size="medium" />}
          to={`/schedule/${search}`}
          $active={isMatchSchedule}
        >
          {isSidebarCollapsed ? '' : 'Schedule'}
        </StyledTextButtonLinkRRD>
      )}
      {shouldShowSettingsTab && (
        <StyledTextButtonLinkRRD
          onClick={handleMenuOnClick}
          $isSidebarCollapsed={isSidebarCollapsed}
          icon={<Icon source={gear} size="medium" />}
          to={settingsTabLink}
          $active={isMatchSettings}
        >
          {isSidebarCollapsed ? '' : 'Settings'}
        </StyledTextButtonLinkRRD>
      )}
    </StyledMenuItemGroup>
  );
};

const Sidebar: React.FC<{
  isSidebarCollapsed?: boolean;
  setIsSidebarCollapsed: (collapse?: boolean) => void;
}> = ({ setIsSidebarCollapsed, isSidebarCollapsed }) => {
  const isMatchLiveDeliveries = Boolean(useMatchLiveDeliveries());

  const isSmallDevice = useIsSmallDevice();
  const showSidebar = () => {
    setIsSidebarCollapsed(false);
  };

  const hideSidebar = () => {
    setIsSidebarCollapsed(true);
  };

  return (
    <Root
      $isSidebarCollapsed={isSidebarCollapsed}
      className={isMatchLiveDeliveries ? elevationClassName.small : ''}
      $isMatchLiveDeliveries={isMatchLiveDeliveries}
    >
      <Layout $isSidebarCollapsed={isSidebarCollapsed}>
        <SidebarMainContentWrapper>
          <StyledHeader
            className="header"
            $isSidebarCollapsed={isSidebarCollapsed}
            icon={isSidebarCollapsed || !isSmallDevice ? 'menu' : 'cross'}
            onMenuIconClick={() => {
              setIsSidebarCollapsed(!isSidebarCollapsed);
            }}
          />

          <NavigationLinks
            isSidebarCollapsed={Boolean(isSidebarCollapsed)}
            showSidebar={showSidebar}
            hideSidebar={isSmallDevice ? hideSidebar : undefined}
          />
        </SidebarMainContentWrapper>
        <Footer $hideFooter={isSidebarCollapsed && isSmallDevice}>
          <StyledUserInfoWithMenu
            onNameClick={isSidebarCollapsed ? showSidebar : undefined}
            className="footer"
            $isSidebarCollapsed={isSidebarCollapsed}
          />
        </Footer>
      </Layout>
    </Root>
  );
};

export default Sidebar;
